/**
 * WOM Configuration for Vessels Client
 *
 * Client-specific configuration including:
 * - API endpoints
 * - Theme colors and styling
 * - Enabled widgets
 * - Custom behavior
 */

/**
 * Get dynamic product title from the page
 * Tries multiple methods to detect the product title
 */
function getProductTitle() {
  // Try different methods to get product title
  const titles = [];

  // Method 1: Try to get from h1 element - try multiple selectors
  const selectors = [
    'h1[itemprop="name"]',
    'h1.product-title',
    '.product__title h1',
    '.product-single__title',
    'h1'
  ];

  for (const selector of selectors) {
    const h1Element = document.querySelector(selector);
    if (h1Element && h1Element.textContent) {
      const h1Title = h1Element.textContent.trim().replace(/\s+/g, ' ').replace(/\n/g, '').replace(/\r/g, '');
      titles.push({ method: `H1 (${selector})`, title: h1Title });
      break;
    }
  }

  // Method 2: Try to get from page <title> tag
  const pageTitle = document.title;
  if (pageTitle) {
    // Remove common suffixes like " | Store" or " – Store" or " - Store"
    let cleanPageTitle = pageTitle;

    // Try different separators
    if (cleanPageTitle.includes(' | ')) {
      cleanPageTitle = cleanPageTitle.split(' | ')[0];
    } else if (cleanPageTitle.includes(' – ')) {
      cleanPageTitle = cleanPageTitle.split(' – ')[0];
    } else if (cleanPageTitle.includes(' - ')) {
      cleanPageTitle = cleanPageTitle.split(' - ')[0];
    }

    cleanPageTitle = cleanPageTitle.trim().replace(/\s+/g, ' ');
    titles.push({ method: 'page title', title: cleanPageTitle });
  }

  // Method 3: Try to get from Shopify product object
  if (typeof window.ShopifyAnalytics !== 'undefined' &&
      window.ShopifyAnalytics.meta &&
      window.ShopifyAnalytics.meta.product &&
      window.ShopifyAnalytics.meta.product.name) {
    const shopifyTitle = window.ShopifyAnalytics.meta.product.name.trim();
    titles.push({ method: 'Shopify Analytics', title: shopifyTitle });
  }

  // Method 4: Try to get from page title meta tag
  const titleMeta = document.querySelector('meta[property="og:title"]');
  if (titleMeta && titleMeta.content) {
    const ogTitle = titleMeta.content.trim();
    titles.push({ method: 'og:title meta', title: ogTitle });
  }

  // Select the longest title (most complete)
  if (titles.length > 0) {
    const longestTitle = titles.reduce((prev, current) =>
      current.title.length > prev.title.length ? current : prev
    );

    return longestTitle.title;
  }

  return null;
}

function getProductHandle() {
  if (
    typeof window.ShopifyAnalytics !== "undefined" &&
    window.ShopifyAnalytics.meta &&
    window.ShopifyAnalytics.meta.product &&
    window.ShopifyAnalytics.meta.product.handle
  ) {
    return window.ShopifyAnalytics.meta.product.handle;
  }

  const urlMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
  if (urlMatch) {
    return decodeURIComponent(urlMatch[1]);
  }

  const ogUrl = document.querySelector('meta[property="og:url"]')?.content;
  if (ogUrl) {
    const handleMatch = ogUrl.match(/\/products\/([^/?#]+)/);
    if (handleMatch) {
      return decodeURIComponent(handleMatch[1]);
    }
  }

  const titleHandle = getProductTitle();
  if (titleHandle) {
    return titleHandle
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-");
  }

  return null;
}

const vesselsWOMConfig = {
  name: "True Brand",

  // API Configuration
  apiBaseUrl: "https://api.thewordofmouth.in",
  companyId: "rivansh", // Company ID from API

  // Dynamic product title getter
  getProductTitle: getProductTitle,
  getProductHandle: getProductHandle,

  // Use explicit product-specific API that works without auth
  reviewsApiUrl: ({ productHandle }) => {
    if (productHandle) {
      return `https://api.thewordofmouth.in/api/reviews/rivansh/${encodeURIComponent(
        productHandle
      )}?offset=0&limit=100`;
    }

    return `https://api.thewordofmouth.in/api/reviews/rivansh/${encodeURIComponent(
      getProductHandle() || ''
    )}?offset=0&limit=100`;
  },

  // Use local JSON data instead of API (set to false to use API)
  useLocalData: false, // Fetch reviews from live API using product handle
  localDataUrl: "/assets/wom-product-data.json", // Path to local JSON file

  // Debug mode
  debug: false,

  // Theme Colors - Customize for TrueBrand
  colors: {
    primary: "#1a1a1a",
    accent: "#C42D2C", // TrueBrand accent color
    background: "#ffffff",
    text: "#374151",
    textLight: "#6b7280",
    border: "#e5e7eb",
    success: "#10b981",
    starColor: "#C42D2C", // Stars with TrueBrand color
  },

  // Typography
  fontFamily:
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",

  // Spacing and Layout
  spacing: {
    cardRadius: "12px",
    cardPadding: "20px",
    gap: "16px",
  },

  // Widget Configuration - Enable/Disable widgets
  components: {
    socialProof: true, // Shows "X reviews in last month"
    liveUsers: true, // Shows "X people viewing now"
    ratingBreakdown: true, // Shows star rating distribution
    reviews: true, // Shows full review cards
  },

  // Override to use company-wide data instead of product-specific
  useCompanyWideData: false, // Using product-specific API that works without auth

  // Widget Settings
  settings: {
    liveUpdates: true, // Auto-refresh live data
    showVerifiedBadge: true, // Show verified purchase badges
    reviewsPerPage: 4, // Number of reviews to show per page
  },

  // Custom Callbacks (Optional)
  onWriteReview: function () {
    // Custom logic when "Write Review" is clicked
    // Add your custom modal logic here
  },

  onLoadMore: function () {
    // Custom logic when "Load More" is clicked
    // Add your custom pagination logic here
  },
};

// Export for use
if (typeof module !== "undefined" && module.exports) {
  module.exports = vesselsWOMConfig;
} else {
  // Expose to window with both names for backward compatibility
  window.vesselsWOMConfig = vesselsWOMConfig; // Primary
  window.clientWOMConfig = vesselsWOMConfig; // Alias for backward compatibility
}
